import type { Task, TodayQueue } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, CalendarClock, CheckCircle2, Flame, Snowflake } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { Confetti } from "../components/Confetti";
import { ModeBadge } from "../components/ModeBadge";
import { ProgressRing } from "../components/ProgressRing";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, LoadingState } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, relativeDays, shortDate } from "../theme";
import { NoticeList } from "./Notices";

export function TodayScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const q = useQuery({ queryKey: ["today"], queryFn: async () => (await callAction<"agency.today.queue", TodayQueue>("agency.today.queue", {})).result });
  const [celebrate, setCelebrate] = useState(false);
  const wasComplete = useRef(false);

  useEffect(() => {
    const p = q.data?.progress;
    if (!p) return;
    const complete = p.total > 0 && p.done >= p.total;
    if (complete && !wasComplete.current) {
      setCelebrate(true);
      const id = window.setTimeout(() => setCelebrate(false), 2000);
      wasComplete.current = true;
      return () => window.clearTimeout(id);
    }
    if (!complete) wasComplete.current = false;
  }, [q.data?.progress]);

  const complete = useMutation({
    mutationFn: (v: { taskId: string; undo: boolean }) => callAction<"agency.task.complete", Task>("agency.task.complete", v),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ["today"] });
      if (!v.undo) {
        const task = q.data?.tasksDue.find((x) => x.id === v.taskId);
        toast({
          title: t("task.completed"),
          body: task?.title,
          tone: "success",
          onUndo: () => complete.mutateAsync({ taskId: v.taskId, undo: true }),
        });
        if (task?.kind === "renewal" && config.agency.features.gamification) toast({ title: t("renewals.niceTouch"), tone: "neutral", durationMs: 2500 });
      } else toast({ title: t("task.undone") });
    },
    onError: (e) => toast({ title: t("common.error"), body: String((e as Error).message), tone: "error" }),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;

  const doNext = () => {
    const task = d.tasksDue[0];
    if (task?.customerId) return navigate(`/customers/${task.customerId}`);
    const ren = d.renewals[0];
    if (ren) return navigate(`/customers/${ren.customerId}`);
    const quote = d.quotesReady[0];
    if (quote) return navigate(`/quotes/${quote.quoteRequestId}`);
    navigate("/renewals");
  };
  const nothingLeft = d.tasksDue.length === 0 && d.renewals.length === 0 && d.quotesReady.length === 0 && d.exceptions.length === 0;

  return (
    <div className="space-y-5">
      <Confetti show={celebrate} />
      <Card className="p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <ProgressRing value={d.progress.total ? d.progress.done / d.progress.total : 1} label={`${d.progress.done}/${d.progress.total}`} />
          <div className="flex-1">
            <p className="text-sm text-ink/60">
              {t("today.greeting")} {d.user.name.split(" ")[0]}
            </p>
            <h1 className="text-3xl font-bold">{t("today.title")}</h1>
            <p className="text-ink/70 mt-1">
              <strong>{Math.round((d.progress.total ? d.progress.done / d.progress.total : 1) * 100)}%</strong> {t("today.progress")}
              {d.unansweredMessages > 0 ? (
                <>
                  {" · "}
                  <span className="text-amber-700 font-semibold">
                    {d.unansweredMessages} {t("today.unanswered")}
                  </span>
                </>
              ) : null}
            </p>
            {config.agency.features.gamification ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge tone="green" className="text-sm py-1">
                  <Flame className="h-4 w-4" /> {d.streak.days} {t("today.streak")}
                </Badge>
                <span className="text-xs text-ink/55">
                  {t("today.streakGoal")}: {d.streak.touchedThisWeek}/{d.streak.dueThisWeek}
                </span>
                {d.streak.freezeAvailable ? (
                  <Badge tone="blue">
                    <Snowflake className="h-3.5 w-3.5" /> {t("today.freeze")}
                  </Badge>
                ) : null}
              </div>
            ) : null}
          </div>
          <Button size="lg" onClick={doNext} className="md:self-center" disabled={nothingLeft}>
            {t("today.next")} <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </Card>

      {nothingLeft ? <EmptyState title={t("today.allDone")} /> : null}

      <Card className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center border-primary/30 bg-primary/5">
        <div className="flex-1">
          <p className="font-bold">{t("today.guide.title")}</p>
          <p className="text-sm text-ink/70">{t("today.guide.body")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/setup")}>{t("today.guide.setup")}</Button>
          <Button onClick={() => navigate("/guide")}>{t("today.guide.open")} <ArrowRight className="h-4 w-4" /></Button>
        </div>
      </Card>

      <div className="grid gap-5 md:grid-cols-2">
        <Card>
          <CardHeader title={t("today.tasks")} action={<Badge>{d.tasksDue.length}</Badge>} />
          <ul className="divide-y divide-ink/5">
            {d.tasksDue.length === 0 ? <li className="px-5 py-4 text-sm text-ink/55">{t("common.empty")}</li> : null}
            {d.tasksDue.map((task) => (
              <li key={task.id} className="flex items-center gap-3 px-5 py-3">
                <button
                  aria-label={t("task.complete")}
                  onClick={() => complete.mutate({ taskId: task.id, undo: false })}
                  className="min-h-tap min-w-tap inline-flex items-center justify-center rounded-full text-ink/30 hover:text-primary"
                >
                  <CheckCircle2 className="h-6 w-6" />
                </button>
                <div className="flex-1 min-w-0">
                  <Link to={task.customerId ? `/customers/${task.customerId}` : "#"} className="font-semibold hover:underline block truncate">
                    {task.title}
                  </Link>
                  <p className="text-xs text-ink/55">
                    {task.customerName ? `${task.customerName} · ` : ""}
                    {t(`task.kind.${task.kind}`)} · {t("common.due")} {relativeDays(daysBetween(task.dueAt), locale)}
                  </p>
                </div>
                {daysBetween(task.dueAt) < 0 ? <Badge tone="red">{relativeDays(daysBetween(task.dueAt), locale)}</Badge> : null}
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <CardHeader title={t("today.renewals")} action={<Link to="/renewals" className="text-sm font-semibold text-primary-dark hover:underline">{t("common.open")}</Link>} />
          <ul className="divide-y divide-ink/5">
            {d.renewals.length === 0 ? <li className="px-5 py-4 text-sm text-ink/55">{t("renewals.none")}</li> : null}
            {d.renewals.slice(0, 8).map((r) => (
              <li key={r.policy.id} className="flex items-center gap-3 px-5 py-3">
                <CalendarClock className="h-5 w-5 text-ink/40 shrink-0" />
                <div className="flex-1 min-w-0">
                  <Link to={`/customers/${r.customerId}`} className="font-semibold hover:underline block truncate">
                    {r.customerName}
                  </Link>
                  <p className="text-xs text-ink/55">
                    {r.policy.carrier} · {r.policy.line} · {shortDate(r.policy.expirationDate, locale)} ({relativeDays(r.daysUntil, locale)})
                  </p>
                </div>
                <DeltaBadge pct={r.premiumDeltaPct} cents={r.premiumDeltaCents} />
                {r.remarket ? <Badge tone="amber">{t("renewals.remarket")}</Badge> : null}
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <CardHeader title={t("today.quotes")} action={<Badge>{d.quotesReady.length}</Badge>} />
          <ul className="divide-y divide-ink/5">
            {d.quotesReady.length === 0 ? <li className="px-5 py-4 text-sm text-ink/55">{t("common.empty")}</li> : null}
            {d.quotesReady.map((qr) => (
              <li key={qr.quoteRequestId} className="flex items-center gap-3 px-5 py-3">
                <div className="flex-1 min-w-0">
                  <Link to={`/quotes/${qr.quoteRequestId}`} className="font-semibold hover:underline block truncate">
                    {qr.customerName}
                  </Link>
                  <p className="text-xs text-ink/55">
                    {qr.carriersReturned} {t("today.carriers")} · {qr.carriersPending} {t("today.pending")} · {t("today.best")} {money(qr.bestPremiumCents)}
                  </p>
                </div>
                <ModeBadge mode={qr.executionMode} />
              </li>
            ))}
          </ul>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader title={t("notices.today")} action={<Link to="/notices" className="text-sm font-semibold text-primary-dark hover:underline">{t("common.open")}</Link>} />
          <div className="px-4 pb-4">
            {d.notices.length === 0 ? <p className="py-2 text-sm text-ink/55">{t("common.empty")}</p> : <NoticeList rows={d.notices.slice(0, 6)} compact />}
          </div>
        </Card>

        <Card>
          <CardHeader title={t("today.exceptions")} action={<Badge tone={d.exceptions.length ? "amber" : "neutral"}>{d.exceptions.length}</Badge>} />
          <ul className="divide-y divide-ink/5">
            {d.exceptions.length === 0 ? <li className="px-5 py-4 text-sm text-ink/55">{t("common.empty")}</li> : null}
            {d.exceptions.map((ex) => (
              <li key={ex.id} className="flex items-center gap-3 px-5 py-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  {ex.quoteRequestId ? (
                    <Link to={`/quotes/${ex.quoteRequestId}`} className="font-semibold hover:underline block truncate text-primary-dark">
                      {ex.title}
                    </Link>
                  ) : ex.customerId ? (
                    <Link to={`/customers/${ex.customerId}`} className="font-semibold hover:underline block truncate">
                      {ex.title}
                    </Link>
                  ) : (
                    <p className="font-semibold truncate">{ex.title}</p>
                  )}
                  <p className="text-xs text-ink/55">
                    {ex.kind === "needs_human" ? t("job.needsYou") : ex.kind === "notice_overdue" ? t("today.kind.notice_overdue") : ex.kind.replace(/_/g, " ")} · {shortDate(ex.since, locale)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

export function DeltaBadge({ pct, cents }: { pct: number | null; cents: number | null }) {
  if (pct == null || cents == null) return <Badge>—</Badge>;
  const tone = pct > 0.15 ? "red" : pct > 0.05 ? "amber" : "green";
  return (
    <Badge tone={tone}>
      {pct > 0 ? "+" : ""}
      {Math.round(pct)}% · {money(cents, { showSign: true })}
    </Badge>
  );
}

export function daysBetween(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}
